import React from "react";
import { motion } from "framer-motion";

/**
 * NetworkVisualization — abstract nodes-and-edges SVG for the marketplace hero.
 * Nodes pulse; edges have a subtle animated dash flow.
 */
const NODES = [
  { id: "hub", x: 300, y: 220, r: 22, hub: true },
  { id: "m1", x: 90, y: 90, r: 12 },
  { id: "m2", x: 130, y: 320, r: 10 },
  { id: "m3", x: 60, y: 220, r: 11 },
  { id: "p1", x: 500, y: 90, r: 12 },
  { id: "p2", x: 540, y: 220, r: 11 },
  { id: "p3", x: 470, y: 350, r: 10 },
  { id: "b1", x: 300, y: 60, r: 9 },
  { id: "b2", x: 300, y: 400, r: 9 },
];

const EDGES = [
  ["hub", "m1"],
  ["hub", "m2"],
  ["hub", "m3"],
  ["hub", "p1"],
  ["hub", "p2"],
  ["hub", "p3"],
  ["hub", "b1"],
  ["hub", "b2"],
  ["m1", "b1"],
  ["p1", "b1"],
];

const findNode = (id) => NODES.find((n) => n.id === id);

export const NetworkVisualization = ({ className }) => {
  return (
    <div className={className} data-testid="network-visualization">
      <svg
        viewBox="0 0 600 460"
        className="h-auto w-full max-w-md"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id="hub-grad" cx="50%" cy="50%">
            <stop offset="0%" stopColor="#00E676" stopOpacity="0.95" />
            <stop offset="70%" stopColor="#00E676" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#00E676" stopOpacity="0" />
          </radialGradient>
          <filter id="glow-net" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="3.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Edges */}
        {EDGES.map(([a, b], i) => {
          const A = findNode(a);
          const B = findNode(b);
          return (
            <motion.line
              key={`${a}-${b}-${i}`}
              x1={A.x}
              y1={A.y}
              x2={B.x}
              y2={B.y}
              stroke="rgba(0,230,118,0.35)"
              strokeWidth="1"
              strokeDasharray="4 6"
              initial={{ pathLength: 0, opacity: 0 }}
              whileInView={{ pathLength: 1, opacity: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: 0.06 * i, duration: 0.8 }}
              style={{
                strokeDashoffset: 0,
                animation: `dashflow 12s linear infinite`,
              }}
            />
          );
        })}

        {/* Nodes */}
        {NODES.map((n, i) => (
          <motion.g
            key={n.id}
            initial={{ opacity: 0, scale: 0 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{
              delay: 0.04 * i,
              type: "spring",
              stiffness: 250,
              damping: 20,
            }}
          >
            {n.hub && (
              <circle
                cx={n.x}
                cy={n.y}
                r={n.r + 24}
                fill="url(#hub-grad)"
              />
            )}
            <circle
              cx={n.x}
              cy={n.y}
              r={n.r}
              fill={n.hub ? "#00E676" : "rgba(0,230,118,0.85)"}
              filter="url(#glow-net)"
            />
            {n.hub && (
              <text
                x={n.x}
                y={n.y + 4}
                textAnchor="middle"
                fill="#080808"
                fontSize="11"
                fontFamily='"Space Grotesk", Inter, sans-serif'
                fontWeight="700"
                letterSpacing="0.5"
              >
                ZenMe
              </text>
            )}
            {/* Pulsing ring on hub */}
            {n.hub && (
              <motion.circle
                cx={n.x}
                cy={n.y}
                r={n.r}
                fill="none"
                stroke="#00E676"
                strokeWidth="1"
                initial={{ scale: 1, opacity: 0.7 }}
                animate={{ scale: 2.4, opacity: 0 }}
                transition={{
                  duration: 2.4,
                  repeat: Infinity,
                  ease: "easeOut",
                }}
                style={{ transformOrigin: `${n.x}px ${n.y}px` }}
              />
            )}
          </motion.g>
        ))}
      </svg>

      <style>{`@keyframes dashflow { to { stroke-dashoffset: -100 } }`}</style>
    </div>
  );
};

export default NetworkVisualization;
